import { useState } from 'react'
import {
  useLicenses,
  useCreateLicense,
  useRevokeLicense,
  type License,
  type CreateLicenseData,
} from '../hooks/useLicenses'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { Plus, Ban, KeyRound, Copy, Check } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

function LicenseStatusBadge({ status }: { status: License['status'] }) {
  const variants: Record<License['status'], { variant: 'default' | 'secondary' | 'destructive'; label: string }> = {
    active: { variant: 'default', label: 'Active' },
    expired: { variant: 'secondary', label: 'Expired' },
    revoked: { variant: 'destructive', label: 'Revoked' },
  }
  const { variant, label } = variants[status]
  return <Badge variant={variant}>{label}</Badge>
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button variant="ghost" size="icon" onClick={handleCopy} className="h-6 w-6">
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  )
}

export function LicensesPage() {
  const { data: licenses, isLoading, error } = useLicenses()
  const createLicense = useCreateLicense()
  const revokeLicense = useRevokeLicense()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [revokeKey, setRevokeKey] = useState<string | null>(null)
  const [newLicenseKey, setNewLicenseKey] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState<CreateLicenseData>({
    customer_email: '',
    customer_name: '',
    plugins: ['*'],
    activations_limit: 5,
    notes: '',
  })

  const handleCreate = async () => {
    try {
      const result = await createLicense.mutateAsync(formData)
      setNewLicenseKey(result.key)
      toast.success('License created successfully')
      setFormData({
        customer_email: '',
        customer_name: '',
        plugins: ['*'],
        activations_limit: 5,
        notes: '',
      })
    } catch (err) {
      toast.error('Failed to create license')
    }
  }

  const handleRevoke = async () => {
    if (!revokeKey) return
    try {
      await revokeLicense.mutateAsync(revokeKey)
      toast.success('License revoked successfully')
      setRevokeKey(null)
    } catch (err) {
      toast.error('Failed to revoke license')
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">Failed to load licenses: {String(error)}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Licenses</h1>
          <p className="text-muted-foreground">Manage license keys for plugins and themes</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={(open) => {
          setIsCreateOpen(open)
          if (!open) setNewLicenseKey(null)
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Create License</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            {newLicenseKey ? (
              <>
                <DialogHeader>
                  <DialogTitle>License Created</DialogTitle>
                  <DialogDescription>
                    Copy this license key now. You won't be able to see it again.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <div className="flex items-center gap-2 rounded-lg bg-muted p-4">
                    <code className="flex-1 text-lg font-mono">{newLicenseKey}</code>
                    <CopyButton text={newLicenseKey} />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => {
                    setIsCreateOpen(false)
                    setNewLicenseKey(null)
                  }}>
                    Done
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Create License</DialogTitle>
                  <DialogDescription>
                    Generate a new license key for a customer.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-4 md:space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Customer Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="customer@example.com"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Customer Name</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="limit">Activations Limit</Label>
                    <Input
                      id="limit"
                      type="number"
                      min={0}
                      placeholder="5"
                      value={formData.activations_limit}
                      onChange={(e) => setFormData({ ...formData, activations_limit: parseInt(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">0 = unlimited</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      placeholder="Internal notes..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={createLicense.isPending || !formData.customer_email}
                  >
                    {createLicense.isPending ? 'Creating...' : 'Create'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="p-4 pb-2 md:p-6 md:pb-2">
          <CardTitle>All Licenses</CardTitle>
          <CardDescription>
            {isLoading ? 'Loading...' : `${licenses?.length || 0} licenses`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : licenses?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <KeyRound className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No licenses yet</h3>
              <p className="text-muted-foreground">
                Create your first license to get started
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>License Key</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Plugins</TableHead>
                  <TableHead>Activations</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {licenses?.map((license) => (
                  <TableRow key={license.key}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono">{license.key}</code>
                        <CopyButton text={license.key} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{license.customer_name || '-'}</p>
                        <p className="text-sm text-muted-foreground">{license.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <LicenseStatusBadge status={license.status} />
                    </TableCell>
                    <TableCell>
                      {license.plugins.includes('*') ? (
                        <Badge variant="outline">All Plugins</Badge>
                      ) : (
                        <span className="text-sm">{license.plugins.join(', ')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{license.activations?.length || 0}</span>
                      <span className="text-muted-foreground">
                        /{license.activations_limit === 0 ? '∞' : license.activations_limit}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {license.created_at
                        ? formatDistanceToNow(new Date(license.created_at), { addSuffix: true })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {license.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setRevokeKey(license.key)}
                        >
                          <Ban className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Revoke Confirmation */}
      <AlertDialog open={!!revokeKey} onOpenChange={() => setRevokeKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke License</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this license? The customer will no longer be able to
              use it for updates or downloads.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {revokeLicense.isPending ? 'Revoking...' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
